import React, { useState, useEffect, useCallback } from 'react';
import { api, API_BASE } from '../utils/api';
import { formatDateThai } from '../utils/helpers';
import { Loader2, AlertCircle, Printer, Filter, CalendarCheck, CalendarX, Paperclip, HelpCircle } from 'lucide-react';

const getStatusBadge = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'pending': return 'รอพิจารณา';
    case 'approved': return 'อนุมัติแล้ว';
    case 'rejected': return 'ไม่อนุมัติ';
    default: return status;
  }
};

// --- [START] ฟังก์ชัน Helper สำหรับไฟล์แนบ ---
const handlePrintAttachment = (attachmentDataUrl) => {
let printWindow;
  try {
    let imageUrl;
    if (attachmentDataUrl.startsWith('data:image')) {
      imageUrl = attachmentDataUrl;
    } else {
      const cleanPath = attachmentDataUrl.startsWith('/') ? attachmentDataUrl.substring(1) : attachmentDataUrl;
      imageUrl = `${API_BASE}${cleanPath}`;
    }

    const printContent = `
      <!DOCTYPE html>
      <html lang="th">
      <head>
        <meta charset="UTF-8">
        <title>เอกสารแนบใบลืมลงเวลา</title>
        <style>
          @font-face { font-family: 'Sarabun'; src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype'); }
          @page { size: A4 portrait; margin: 1.5cm; }
          body { 
            margin: 0; 
            padding: 0; 
            font-family: 'Sarabun', sans-serif; 
            word-break: keep-all;
            line-height: 1;
          }
          .container { width: 100%; text-align: center; }
          img { max-width: 100%; max-height: 95vh; height: auto; width: auto; object-fit: contain; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="container">
          <img src="${imageUrl}" alt="เอกสารแนบ" /> </div>
      </body>
      </html>
    `;
    
    printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('ไม่สามารถเปิดหน้าต่างสำหรับพิมพ์ได้ กรุณาตรวจสอบการตั้งค่า Pop-up Blocker');
      return;
    }
    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus(); 
      printWindow.print();
    }, 1000); 

  } catch (err) {
    console.error("Error printing attachment:", err);
    if (printWindow) {
      printWindow.document.write(`<h3 style="font-family: Sarabun, sans-serif; color: red;">เกิดข้อผิดพลาด: ${err.message}</h3>`);
    } else {
      alert("เกิดข้อผิดพลาดในการแสดงไฟล์แนบ: " + err.message);
    }
  }
};
// --- [END] สิ้นสุดฟังก์ชัน Helper สำหรับไฟล์แนบ ---

const ForgotScanApproval = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [actionLoading, setActionLoading] = useState({});
  const [downloadLoading, setDownloadLoading] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getForgotScanRequests();
      setRequests(data);
    } catch (err) {
      setError('ไม่สามารถโหลดข้อมูลคำร้องได้: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (requestId, action) => {
    const actionText = action === 'approve' ? 'อนุมัติ' : 'ไม่อนุมัติ';
    if (!window.confirm(`คุณต้องการ ${actionText} รายการนี้ใช่หรือไม่?`)) return;

    setActionLoading(prev => ({ ...prev, [requestId]: true }));
    try {
      const apiCall = action === 'approve' ? api.approveForgotScanRequest(requestId, user.employee_id) : api.rejectForgotScanRequest(requestId, user.employee_id);
      await apiCall;
      await fetchData();
    } catch (err) {
      alert(`เกิดข้อผิดพลาด: ` + err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDownloadDocx = (request) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${API_BASE}forgot-scan-request-download-docx?request_id=${request.id}&token=${token}`, '_blank');
  };

  const currentYear = new Date().getFullYear();
  const allYears = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const filteredRequests = requests.filter(req => {
    if (new Date(req.request_date).getFullYear() !== selectedYear) return false;
    if (filterStatus === 'all') return true;
    return req.status === filterStatus;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="space-y-8">
      <div><h1 className="text-3xl font-bold text-gray-900">จัดการใบลืมลงเวลา</h1></div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <div className="flex space-x-2">
            {['pending', 'approved', 'rejected', 'all'].map(status => (
               <button key={status} onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${filterStatus === status ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {status === 'pending' ? 'รอพิจารณา' : status === 'approved' ? 'อนุมัติแล้ว' : status === 'rejected' ? 'ไม่อนุมัติ' : 'ทั้งหมด'}
              </button>
            ))}
          </div>
          <div>
            <label className="mr-2 text-sm">เลือกปี:</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="border rounded-md p-2">
              {allYears.map(year => <option key={year} value={year}>{year + 543}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-0 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin h-10 w-10 text-blue-500"/></div>
          ) : error ? (
             <div className="p-4 m-6 bg-red-50 text-red-700 flex items-center"><AlertCircle className="mr-2 h-5 w-5"/>{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ผู้ยื่น</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่ลืม</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่ยื่น</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">เหตุผล</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">ไม่พบข้อมูล</td></tr>
                  ) : filteredRequests.map(req => (
                    <tr key={req.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{req.first_name} {req.last_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{formatDateThai(req.request_date)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDateThai(req.created_at)}</td>
                      <td className="px-6 py-4"><span className={`px-2 inline-flex text-xs font-semibold rounded-full ${req.scan_type === 'in' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>{req.scan_type === 'in' ? 'ลืมเข้า' : 'ลืมออก'}</span></td>
                      <td className="px-6 py-4 text-sm text-gray-700 truncate max-w-xs" title={req.reason}>{req.reason}</td>
                      
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(req.status)}`}>
                          {getStatusText(req.status)}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {req.attachment_image && (
                            <button 
                                title="ดู/พิมพ์ หลักฐานแนบ"
                                onClick={() => handlePrintAttachment(req.attachment_image)} 
                                className="p-2 text-purple-600 hover:bg-purple-100 rounded-md"
                            >
                                <Paperclip className="h-5 w-5" />
                            </button>
                          )}

                          {/* แสดง Spinner ตอนกำลังโหลดไฟล์ Word */}
                          <button
                            title="ดาวน์โหลด Word"
                            onClick={() => handleDownloadDocx(req)}
                            disabled={downloadLoading[req.id]}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-md disabled:opacity-50"
                          >
                            {downloadLoading[req.id] ? <Loader2 className="h-5 w-5 animate-spin"/> : <Printer className="h-5 w-5" />}
                          </button>
                          
                          {(req.status === 'pending' || req.status === 'rejected') && (
                            <button 
                                onClick={() => handleAction(req.id, 'approve')} 
                                disabled={actionLoading[req.id]} 
                                title={req.status === 'rejected' ? 'เปลี่ยนเป็น อนุมัติ' : 'อนุมัติ'}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-md disabled:opacity-50"
                            >
                                {actionLoading[req.id] ? <Loader2 className="h-5 w-5 animate-spin"/> : <CalendarCheck className="h-5 w-5"/>}
                            </button>
                          )}

                          {(req.status === 'pending' || req.status === 'approved') && (
                              <button 
                                onClick={() => handleAction(req.id, 'reject')} 
                                disabled={actionLoading[req.id]} 
                                title={req.status === 'approved' ? 'ยกเลิกการอนุมัติ (เปลี่ยนเป็น ไม่อนุมัติ)' : 'ไม่อนุมัติ'}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-md disabled:opacity-50"
                              >
                                {actionLoading[req.id] ? <Loader2 className="h-5 w-5 animate-spin"/> : <CalendarX className="h-5 w-5"/>}
                              </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotScanApproval;